import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation, Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Grid, Typography } from "@material-ui/core";
import AmoTableWithFilters from "components/table/AmoTableWithFilters";
import { useCommunicationsUserHooks } from "./hooks/userDataHooks";
import { useGlobalStyles } from "hooks/globalStylesHook";
import { useSnackbar } from "contexts/SnackbarContext";
import { displayTypes, tableDefaultActions } from "constants/amoTableConstants";
import colors from "constants/colors";
import { errorMessages } from "constants/errorMessages";
import { snackbarTypes } from "constants/snackbar";
import { formatToPhoneWithExtension } from "utils/number";
import { formatISODateDashStyle } from "utils/date";
import { booleanToTextOrUndefined } from "utils/string";
import { exportToExcel } from "utils/export";
import CheckedIcon from "./CheckedIcon";

const useStyles = makeStyles((theme) => ({
  email: {
    color: colors.green.main,
    textDecoration: "none",
    justifyContent: "flex-start",
    fontWeight: "bold",
    marginLeft: "-0.375rem",
    paddingTop: "0",
    paddingBottom: "0",
    "&:hover": {
      textDecoration: "underline",
      backgroundColor: "transparent",
      cursor: "pointer",
    },
  },
}));

/**
 * A table component for displaying Communication users
 *
 * @param {object} props
 * @returns {React.Component} The table component
 */
const CommunicationUserTable = (props) => {
  const classes = useStyles();
  const { id, tableTitleComponent } = props;

  const history = useHistory();
  const location = useLocation();
  const globalClasses = useGlobalStyles();
  const { showSnackbar } = useSnackbar();

  const [focusedItem, setFocusedItem] = useState({});
  const rowRefs = useRef({});
  const filteredData = useRef([]);

  const {
    data: userData,
    isFetching: dataFetching,
  } = useCommunicationsUserHooks();

  const zoomString = (phone, phoneExtension) => {
    const cleanPhone = phone ? phone.replace(/[^0-9.]+/g, "") : "";
    const cleanExtension = phoneExtension
      ? `?callerid={${phoneExtension}}`
      : "";

    return `zoomphonecall://+1${cleanPhone}${cleanExtension}`;
  };

  const tableColumns = [
    {
      fieldKey: "name",
      label: "Name",
      widthPerc: 20,
      rowColor: "primary",
      rowVariant: "body2",
      customRender: (user) => (
        <Link
          to={`/users/amo/${user.id}${location.search}`}
          ref={(element) => {
            rowRefs.current[user.id] = element;
          }}
          onFocus={() => setFocusedItem({ id: user.id })}
          className={globalClasses.tableActionCell}
        >
          {user.name}
        </Link>
      ),
      isCustom: true,
    },
    { fieldKey: "organization", label: "Organization", widthPerc: 20 },
    {
      fieldKey: "email",
      label: "Email",
      widthPerc: 20,
      customRender: (item) => (
        <Button
          className={classes.email}
          align="left"
          href={`mailto:${item.email}`}
          disableRipple
          style={{ backgroundColor: "transparent" }}
          onClick={(elem) => elem.stopPropagation()}
        >
          {item.email}
        </Button>
      ),
      isCustom: true,
    },
    {
      fieldKey: "phone",
      label: "Phone #",
      customRender: (item) => (
        <Button
          className={classes.email}
          align="left"
          href={zoomString(item.phone, item.phoneExtension)}
          disableRipple
          style={{ backgroundColor: "transparent" }}
          onClick={(elem) => elem.stopPropagation()}
        >
          {formatToPhoneWithExtension(item.phone, item.phoneExtension)}
        </Button>
      ),
      isCustom: true,
    },
    {
      fieldKey: "lastLoginDate",
      label: "Last Login",
      customRender: (item) =>
        item.lastLoginDate !== "1970-01-01T00:00:00" ? (
          <Typography>
            <Grid item xs>
              {formatISODateDashStyle(item.lastLoginDate)}{" "}
            </Grid>
          </Typography>
        ) : (
          <></>
        ),
      isCustom: true,
      isDate: true,
      displayType: displayTypes.date,
    },
    {
      fieldKey: "enabled",
      label: "Enabled",
      customRender: (item) => <CheckedIcon checked={item.enabled} />,
      customExport: (item) => booleanToTextOrUndefined(item.enabled) ?? "",
      displayType: displayTypes.boolean,
      isCustom: true,
      horizontalAlign: "center",
    },
  ];

  const handleItemClick = (user) => {
    history.push(`/users/communication/${user.id}${location.search}`);
  };

  const tableActions = {
    titleComponent: tableTitleComponent,
    actions: [
      tableDefaultActions.new,
      tableDefaultActions.search,
      tableDefaultActions.export,
    ],
    actionsCustomConfigs: {
      [tableDefaultActions.new]: {
        onClick: () => {
          history.push(`/users/communication/new${location.search}`);
        },
      },
      [tableDefaultActions.export]: {
        onClick: () => {
          try {
            exportToExcel(
              filteredData.current,
              tableColumns,
              "Communications Staff"
            );
          } catch (error) {
            console.error(error);

            showSnackbar(
              errorMessages.creatingSpreadsheet,
              snackbarTypes.error
            );
          }
        },
      },
    },
  };

  return (
    <AmoTableWithFilters
      id={id}
      testId="communicationUserTable"
      items={dataFetching ? [] : userData}
      columns={tableColumns}
      defaultOrderBy="name"
      enableItemClick
      onItemClick={handleItemClick}
      onFilteredDataChanged={(value) => {
        filteredData.current = value;
      }}
      onScroll={(item) => {
        rowRefs.current?.[item.id]?.focus();
      }}
      focusedItem={focusedItem}
      actionsProps={tableActions}
      isLoading={dataFetching}
    />
  );
};

// set the prop-types for this component
CommunicationUserTable.propTypes = {
  id: PropTypes.string,
  tableTitleComponent: PropTypes.node,
};

CommunicationUserTable.defaultProps = {
  id: "communication-user-table",
  tableTitleComponent: PropTypes.node,
};

export default CommunicationUserTable;
